/**
 * Central session + auth helpers.
 *  - Stores session tokens per role (admin, agent, referrer, staff).
 *  - Patches window.fetch so every call to our API carries `Authorization: Bearer`.
 *  - Keeps a short-lived download token for <a href> / <img src> / window.open
 *    (`authedUrl(url)` appends `?dt=`), refreshed automatically.
 *  - Emits `utmost-auth-expired` (detail: role) on a 401 so portals can log out.
 */
const API_URL = process.env.REACT_APP_BACKEND_URL;

const KEYS = {
  admin: ['sessionStorage', 'utmost_admin_token'],
  agent: ['sessionStorage', 'utmost_agent_token'],
  referrer: ['localStorage', 'utmost_referrer_token'],
  staff: ['localStorage', 'utmost_va_token'],
};
const DL_KEY = 'utmost_dl_token';

const store = (kind) => (kind === 'localStorage' ? window.localStorage : window.sessionStorage);

export const getToken = (role) => {
  const [kind, key] = KEYS[role];
  return store(kind).getItem(key) || '';
};

export const setSession = (role, token) => {
  const [kind, key] = KEYS[role];
  if (token) store(kind).setItem(key, token); else store(kind).removeItem(key);
  if (!token) clearDownloadToken(); else ensureDownloadToken(true);
};

export const clearSession = (role) => setSession(role, '');

const roleForPath = (pathname) => {
  if (pathname.startsWith('/admin')) return 'admin';
  if (pathname.startsWith('/time-tracker')) return 'staff';
  if (pathname.startsWith('/referrer-login')) return 'referrer';
  if (pathname.startsWith('/agent-login')) return 'agent';
  return null;
};

export const currentToken = () => {
  const preferred = roleForPath(window.location.pathname);
  const order = preferred ? [preferred, 'agent', 'admin', 'referrer', 'staff'] : ['agent', 'admin', 'referrer', 'staff'];
  for (const r of order) { const t = getToken(r); if (t) return { role: r, token: t }; }
  return null;
};

// ---- download tokens ------------------------------------------------------
const readDl = () => {
  try { const d = JSON.parse(sessionStorage.getItem(DL_KEY) || 'null'); return d && d.exp > Date.now() ? d : null; }
  catch { return null; }
};
export const setDownloadToken = (token, expiresInSec = 1800) => {
  sessionStorage.setItem(DL_KEY, JSON.stringify({ token, exp: Date.now() + (expiresInSec - 60) * 1000 }));
};
const clearDownloadToken = () => sessionStorage.removeItem(DL_KEY);

let refreshing = null;
export const ensureDownloadToken = async (force = false) => {
  const cur = readDl();
  if (!force && cur && cur.exp - Date.now() > 5 * 60 * 1000) return cur.token;
  if (refreshing) return refreshing;
  const sess = currentToken();
  if (!sess) return null;
  refreshing = (async () => {
    try {
      const r = await fetch(`${API_URL}/api/auth/download-token`, { method: 'POST', headers: { Authorization: `Bearer ${sess.token}` } });
      if (!r.ok) return null;
      const d = await r.json();
      setDownloadToken(d.download_token, d.expires_in);
      return d.download_token;
    } catch { return null; }
    finally { refreshing = null; }
  })();
  return refreshing;
};

/** Append the download token so <a href>/<img src>/window.open can authenticate. */
export const authedUrl = (url) => {
  if (!url) return url;
  const dl = readDl();
  const sess = currentToken();
  const token = dl ? dl.token : sess ? sess.token : '';
  if (!token) return url;
  if (!dl) ensureDownloadToken(true);
  const clean = url.replace(/([?&])admin_password=[^&]*&?/g, '$1').replace(/[?&]$/, '');
  return `${clean}${clean.includes('?') ? '&' : '?'}dt=${encodeURIComponent(token)}`;
};

/** onClick handler for <a href="…api…"> links: opens with a fresh token at click time. */
export const openAuthed = (e) => {
  const href = e.currentTarget?.getAttribute('href');
  if (!href) return;
  e.preventDefault();
  window.open(authedUrl(href), '_blank', 'noopener');
};

// ---- global fetch patch ---------------------------------------------------
export const installAuthFetch = () => {
  if (window.__utmostAuthFetchInstalled) return;
  window.__utmostAuthFetchInstalled = true;
  const original = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url || '';
    const isApi = url.startsWith(`${API_URL}/api`) || url.startsWith('/api');
    let role = null;
    if (isApi) {
      const headers = new Headers(init.headers || (typeof input !== 'string' ? input.headers : undefined) || {});
      if (!headers.has('Authorization')) {
        const sess = currentToken();
        if (sess) { headers.set('Authorization', `Bearer ${sess.token}`); role = sess.role; }
      }
      init = { ...init, headers };
    }
    const res = await original(input, init);
    if (isApi && res.status === 401 && role && !url.includes('/login')) {
      window.dispatchEvent(new CustomEvent('utmost-auth-expired', { detail: { role } }));
    }
    return res;
  };
  if (currentToken()) ensureDownloadToken();
  setInterval(() => { if (currentToken()) ensureDownloadToken(); }, 5 * 60 * 1000);
};
